<template>
  <div class="viewer_container" v-if="isShowViewer" :class="{ viewer_expanded: isVideoExpanded }">
    <ArrowLeftOutlined class="viewer_back" @click.stop="closeViewer" />
    <div class="viewer_wrapper">
      <div class="viewer_content">
        <div
          :class="{ viewer_player_h: currentVideo.type == 'h', viewer_player_v: currentVideo.type == 'v' }"
          class="viewer_player_box"
          style="position: relative;"
        >
          <ArrowLeftOutlined v-if="isVideoExpanded" class="viewer_fullscreen_exit" @click.stop="closeViewer" />
          <video
            ref="videoPlayer"
            :poster="posterSrc(currentVideo)"
            controls
            controlslist="nodownload"
            oncontextmenu="return false;"
            class="viewer_player"
            id="currentVideoPlayer"
            :src="playSrc(currentVideo)"
            playsinline
            webkit-playsinline
            @loadedmetadata="getVideoType()"
            autoplay
          ></video>
        </div>
      </div>
      <div v-if="videoList.length >= 2" class="viewer_list">
        <div class="viewer_item" v-for="(item, index) in videoList" :key="index" @click="handleSelectItem(item)">
          <img
            :src="posterSrc(item)"
            :class="{
              video_playing: currentVideo.resumeVideoId == item.resumeVideoId,
            }"
            oncontextmenu="return false;"
            class="item_player"
          />
          <span v-show="videoDurationList[index]" class="video_duration">
            {{ formatToMinutes(videoDurationList[index]) }}
          </span>
          <div class="item_title">{{ item.jobCategoryName || item.title }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ArrowLeftOutlined } from '@ant-design/icons-vue';
import { defineExpose, reactive, ref } from 'vue';

import { playSrc, posterSrc } from '@/utils/vod.js';

const isShowViewer = ref(false);
const videoPlayer = ref(null);
// 手机端使用浮层内 CSS 沉浸式"全屏"（不调用浏览器原生 Fullscreen API）：
// 微信/iOS/安卓各端行为一致，退出按钮只收起沉浸态、不会把视频整个关掉
const isVideoExpanded = ref(false);

const videoList = ref([]);
const currentVideo = reactive({
  videoUrl: '',
  playUrl: '',
  coverUrl: '',
  jobCategoryName: '',
  resumeVideoId: '',
  type: '',
});
const assignCurrent = (item) => {
  currentVideo.videoUrl = item.videoUrl;
  currentVideo.playUrl = item.playUrl;
  currentVideo.coverUrl = item.coverUrl;
  currentVideo.jobCategoryName = item.jobCategoryName;
  currentVideo.resumeVideoId = item.resumeVideoId;
};

const playCurrent = () => {
  setTimeout(() => {
    if (!videoPlayer.value || !playSrc(currentVideo)) return;
    const playPromise = videoPlayer.value.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {});
    }
  }, 100);
};

const openViewer = (list, index) => {
  videoList.value = list;
  assignCurrent(videoList.value[index]);
  isShowViewer.value = true;
  isVideoExpanded.value = isMobileDevice();
  document.body.style.overflow = 'hidden';
  playCurrent();
};

const closeViewer = () => {
  isVideoExpanded.value = false;
  exitNativeFullscreen();
  isShowViewer.value = false;
  document.body.style.overflow = 'auto';
};

const handleSelectItem = (item) => {
  assignCurrent(item);
  isVideoExpanded.value = isMobileDevice();
  playCurrent();
};

const videoDurationList = ref([]);
const getVideoDuration = (index) => {
  const videoElement = document.getElementById('videoViewerListPlayer' + index);
  const duration = videoElement.duration;
  videoDurationList.value[index] = duration;
};

// 检测是否为移动设备
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// 兜底：若用户通过视频原生控件进入过系统全屏，退出沉浸式时一并退出系统全屏
const exitNativeFullscreen = () => {
  if (document.fullscreenElement && document.exitFullscreen) {
    document.exitFullscreen();
  } else if (document.webkitFullscreenElement && document.webkitExitFullscreen) {
    document.webkitExitFullscreen();
  } else if (document.mozFullScreenElement && document.mozCancelFullScreen) {
    document.mozCancelFullScreen();
  } else if (document.msFullscreenElement && document.msExitFullscreen) {
    document.msExitFullscreen();
  }
};

const getVideoType = () => {
  const videoElement = document.getElementById('currentVideoPlayer');

  const width = videoElement.videoWidth;
  const height = videoElement.videoHeight;

  if (width > height) {
    currentVideo.type = 'h';
  } else {
    currentVideo.type = 'v';
  }
};

const formatToMinutes = (seconds) => {
  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

defineExpose({
  openViewer,
  closeViewer,
});
</script>

<style lang="less" scoped>
.viewer_container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  .viewer_back {
    font-size: 18px;
    color: #fff;
    position: absolute;
    left: 30px;
    top: 30px;
    z-index: 99999;
    cursor: pointer;
  }
  // 全屏态视频左上角退出按钮：半透明圆形，热区足够大，避免被视频层挡住点不到
  .viewer_fullscreen_exit {
    position: absolute;
    left: 16px;
    top: 16px;
    z-index: 10;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    font-size: 20px;
    cursor: pointer;
    pointer-events: auto;
  }
  // 手机端沉浸式"全屏"（纯 CSS，非浏览器原生全屏）：播放器铺满整个浮层，左上角退出按钮直接关闭视频。
  // 选择器特异性高于 1080/780 媒体块里的 wrapper/content 规则，保证小屏也能铺满
  &.viewer_expanded {
    background: #000;
    .viewer_back {
      display: none; // 沉浸态只保留视频左上角的退出按钮，避免两个箭头
    }
    .viewer_wrapper {
      padding: 0;
      .viewer_content {
        width: 100%;
        height: 100%;
        // 必须沿用真实 DOM 层级(.viewer_wrapper .viewer_content)嵌套，
        // 否则编译后选择器类数与基础规则相同、基础规则靠后会赢，圆角去不掉
        .viewer_player_box {
          width: 100%;
          height: 100%;
          background: #000;
          .viewer_player {
            width: 100%;
            height: 100%;
            object-fit: contain; // 横屏视频：竖屏手机里上下留黑边，避免大面积裁切
            border-radius: 0;
          }
          // 竖屏视频比例(约9:16)比手机屏幕(约9:19.5)更宽，contain 会上下留黑条；
          // 竖屏改用 cover 铺满整屏（仅左右轻微裁切），消除上下黑边
          &.viewer_player_v .viewer_player {
            object-fit: cover;
          }
        }
      }
    }
    .viewer_list {
      display: none;
    }
  }
  .viewer_wrapper {
    margin: 0 auto;
    padding: 0 200px;
    display: flex;
    flex-wrap: wrap;
    height: 100%;
    align-items: center;
    justify-content: center;
    max-width: 2560px;
    .viewer_content {
      text-align: center;
      width: calc(100% - 218px);
      height: calc(100% - 100px);
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      .viewer_player {
        border-radius: 20px;
        width: 100%;
        height: 100%;
      }
      .viewer_player_h {
        width: 100%;
        position: relative;
      }
      .viewer_player_v {
        height: 100%;
        position: relative;
      }
    }
    .viewer_list {
      width: 188px;
      margin-left: 30px;
      margin-top: 30px;
      max-height: 80%;
      overflow-y: scroll;
      &::-webkit-scrollbar {
        display: none;
      }
      .viewer_item {
        width: 100%;
        margin: 24px auto;
        cursor: pointer;
        position: relative;
        .item_player {
          width: 100%;
          height: auto;
          border-radius: 8px;
          background: #333333;
        }
        .item_title {
          font-size: 12px;
          font-weight: 500;
          color: #fff;
          margin-top: 12px;
        }
        .video_duration {
          color: #ffffff;
          font-size: 12px;
          background: #000000;
          border-radius: 2px;
          position: absolute;
          bottom: 45px;
          right: 15px;
          padding: 2px 5px;
        }
      }
      .video_playing {
        border: 4px solid #05644f;
      }
    }
  }
  @media (max-width: 1080px) {
    .viewer_wrapper {
      margin: 0 auto;
      padding: 0 100px;
      .viewer_content {
        width: 100%;
        height: 45%;
      }
    }
    .viewer_list {
      display: none;
    }
  }
  @media (max-width: 780px) {
    .viewer_wrapper {
      padding: 0 50px;
    }
  }
}
</style>
